import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthSession } from '../interfaces/auth';
import { Notifier } from '../interfaces/notifier';
import { AuthService } from '../services/auth/auth.service';
import { NotifierService } from '../services/notifier.service';

@Injectable({
  providedIn: 'root',
})
export class AuthInterceptorService implements HttpInterceptor {
  private readonly SKIP_HEADER = 'skip';
  private readonly REFRESH_ENDPOINT = '/api/auth/open/refresh';
  private readonly INVALID_TOKEN_MESSAGE = 'INVALID_TOKEN';

  private authService?: AuthService;

  constructor(
    private injector: Injector,
    private notifierService: NotifierService,
    private snackbarService: MatSnackBar,
  ) {}

  private getAuthService(): AuthService {
    if (!this.authService) {
      this.authService = this.injector.get(AuthService);
    }
    return this.authService;
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (this.shouldSkipInterceptor(req)) {
      return this.handleRequestWithoutAuth(req, next);
    }

    const authService = this.getAuthService();
    const token = authService.getAuthSession()?.token;

    if (!token) {
      return throwError(
        new HttpErrorResponse({
          error: 'Não existe um token de autenticação disponível.',
          status: 401,
        }),
      );
    }

    const authReq = this.addAuthHeader(req, token);

    return next.handle(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        if (this.isValidationError(error)) {
          this.showValidationError();
        }

        if (this.shouldRefreshToken(error, req)) {
          return this.handleTokenRefresh(req, next);
        }

        return throwError(error);
      }),
    );
  }

  private shouldSkipInterceptor(req: HttpRequest<any>): boolean {
    return req.headers.has(this.SKIP_HEADER);
  }

  private handleRequestWithoutAuth(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const modifiedReq = req.clone({
      headers: req.headers.delete(this.SKIP_HEADER),
    });
    return next.handle(modifiedReq).pipe(
      catchError((error: HttpErrorResponse) => {
        if (this.isValidationError(error)) {
          this.showValidationError();
        }
        return throwError(error);
      }),
    );
  }

  private addAuthHeader(
    req: HttpRequest<any>,
    token: string,
  ): HttpRequest<any> {
    return req.clone({
      setHeaders: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private shouldRefreshToken(
    error: HttpErrorResponse,
    req: HttpRequest<any>,
  ): boolean {
    if (req.url.includes(this.REFRESH_ENDPOINT)) {
      return false;
    }

    const is401 = error.status === 401;
    const is403WithInvalidToken =
      error.status === 403 ||
      error.error?.customMessage === this.INVALID_TOKEN_MESSAGE;

    return is401 || is403WithInvalidToken;
  }

  private handleTokenRefresh(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const authService = this.getAuthService();

    if (authService.refreshTokenInProgress && authService.refreshInProgress$) {
      return authService.refreshTokenSubject$.pipe(
        filter((result): result is AuthSession => result !== null),
        take(1),
        switchMap((newSession) => {
          const newAuthReq = this.addAuthHeader(req, newSession.token);
          return next.handle(newAuthReq);
        }),
        catchError((error) => {
          console.error('Erro ao aguardar refresh:', error.message || error);
          return throwError(error);
        }),
      );
    }

    return authService.newRefreshToken().pipe(
      switchMap((newSession) => {
        const newAuthReq = this.addAuthHeader(req, newSession.token);
        return next.handle(newAuthReq);
      }),
      catchError((refreshError) => {
        console.error(
          'Erro no refresh token:',
          refreshError.message || refreshError,
        );

        if (refreshError?.status === 401 || refreshError?.status === 403) {
          authService.logoutByInvalidToken();

          this.snackbarService.open(
            'Sua sessão expirou. Por favor, faca login novamente.',
            'OK',
            {
              duration: 4000, //  4 seconds
            },
          );
        }

        return throwError(refreshError);
      }),
    );
  }

  private isValidationError(error: HttpErrorResponse): boolean {
    return error.error && Array.isArray(error.error);
  }

  private showValidationError(): void {
    const errorNotification: Notifier = {
      displayMessage: 'Ops! Algo não saiu como esperado.',
      messageType: 'errorNotifier',
      buttonTxt: 'OK',
    };
    this.notifierService.showNotification(errorNotification);
  }
}
